import {
  Box,
  Card,
  CardBody,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  StackDivider,
  Table,
  TableContainer,
  Tbody,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
} from "@chakra-ui/react";
import HistoryRecord from "./HistoryRecord";
import { HistoryCategory } from "@/lib/types";
import { EVENT_LIBRARY, HISTORY_TEMPLATES } from "@/lib/constants";
import { BsThreeDotsVertical } from "react-icons/bs";
import useAnalytics from "@/lib/hooks/useAnalytics";
import { useContext, useRef, useState } from "react";
import { AuthContext } from "@/providers/auth";

const HistoryRecordsTable = ({
  category,
  cells,
  records,
  onEdit,
  deleteAction,
  getRecords,
}: {
  category: HistoryCategory;
  cells: any[];
  records: any[];
  onEdit: (id: number) => void;
  deleteAction: Function;
  getRecords: Function;
}) => {
  const { getUserDetails } = useContext(AuthContext);
  const toast = useToast();
  const track = useAnalytics();
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const columns = HISTORY_TEMPLATES[category].columns || [];

  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef(null);

  const onDelete = async () => {
    await deleteAction(deleteId);
    toast({
      status: "success",
      title: "Record Deleted",
      description: "Record has been successfully deleted.",
    });
    track(EVENT_LIBRARY.DELETED_HISTORY, {
      category,
    });

    await getRecords();
    await getUserDetails();
  };

  const onStartDelete = (id: number) => {
    setDeleteId(id);
    onOpen();
  };

  return records.length > 0 ? (
    <>
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Record
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this record? This action cannot be
              undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Nevermind
              </Button>
              <Button colorScheme="red" onClick={onDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <TableContainer w="100%" hideBelow="lg">
        <Table variant="simple">
          <Thead bg="#F9F9F9">
            <Tr>
              {columns.map((column) => (
                <Th key={column}>{column}</Th>
              ))}
              <Th isNumeric>Actions</Th>
            </Tr>
          </Thead>
          <Tbody fontSize="14px" fontWeight="500">
            {records.map((record, index) => (
              <HistoryRecord
                key={record.id}
                record={record}
                cells={cells[index]}
                onEdit={onEdit}
                onDelete={onStartDelete}
              />
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      {records.map((record: any, index: number) => (
        <Card
          key={record.id}
          hideFrom="lg"
          w="100%"
          boxShadow="0px"
          border="1px solid #E4E4E4"
          borderRadius="4px"
          mb="16px"
        >
          <Menu>
            <MenuButton
              as={IconButton}
              variant="ghost"
              aria-label="Record Actions"
              borderRadius="4px"
              position="absolute"
              right="0px"
              top="0px"
              icon={<BsThreeDotsVertical />}
            />
            <MenuList>
              <MenuItem onClick={() => onEdit(record.id)}>Edit</MenuItem>
              <MenuItem onClick={() => onStartDelete(record.id)}>
                Delete
              </MenuItem>
            </MenuList>
          </Menu>

          <CardBody>
            <Stack divider={<StackDivider />} spacing="4">
              {cells[index].map((cell: any, i: number) => (
                <Box key={`${i}-${cell}`}>
                  <Heading
                    size="xs"
                    textTransform="uppercase"
                    color="brand.gray"
                  >
                    {columns[i]}
                  </Heading>
                  <Box pt="2">{cell}</Box>
                </Box>
              ))}
            </Stack>
          </CardBody>
        </Card>
      ))}
    </>
  ) : (
    <Heading
      as="h4"
      color="brand.gray"
      fontSize="16px"
      fontWeight="400"
      textAlign="center"
    >
      You haven&apos;t added any records yet...
    </Heading>
  );
};

export default HistoryRecordsTable;
